// ============================================================================
// SETUP CHECKLIST (Setup > Setup Checklist)
// ============================================================================
// crs-doc/CRS-Setup-Navigation-and-Help-Design.md section 5. One page for the whole tenant: a tenant
// block, then a block per property (and per outlet, slice B). Each row says Done, Missing or Warning,
// why, and has a Go link to the screen that fixes it. Alerts only; worked out live on every open.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { setupService, type SetupChecklist, type SetupChecklistBlock, type SetupChecklistItem } from 'api/services/setupService';
import { canSeeModule } from 'components/modules/print-module/printUi';
import { requestOutletSection } from 'components/modules/dineops-module/masters/outletWorkspace';
import { openHelp } from 'components/help/helpEvents';

interface SetupChecklistScreenProps {
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
  onNavigateModule?: (moduleId: string) => void;
}

const STATUS_LABEL: Record<SetupChecklistItem['status'], string> = {
  DONE: 'Done', MISSING: 'Missing', WARNING: 'Warning', NOT_NEEDED: 'Not needed', INFO: 'Info',
};

const STATUS_CLASS: Record<SetupChecklistItem['status'], string> = {
  DONE: 'badge-success', MISSING: 'badge-danger', WARNING: 'badge-warning', NOT_NEEDED: 'badge-secondary', INFO: 'badge-info',
};

const SetupChecklistScreen: React.FC<SetupChecklistScreenProps> = ({ onNavigateModule }) => {
  const [data, setData] = useState<SetupChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNotNeeded, setShowNotNeeded] = useState(false);
  const [blockFilter, setBlockFilter] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const checklist = await setupService.getChecklist();
      setData(checklist);
      // Blocks with nothing to do start collapsed, so the page opens on what needs attention.
      setCollapsed(new Set(checklist.blocks.filter((b) => b.missing + b.warnings === 0).map(blockKey)));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The setup checklist could not be worked out.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const notNeededCount = useMemo(
    () => (data?.blocks ?? []).reduce((n, b) => n + b.items.filter((i) => i.status === 'NOT_NEEDED').length, 0),
    [data],
  );

  const blocks = useMemo(
    () => (data?.blocks ?? []).filter((b) => !blockFilter || blockKey(b) === blockFilter),
    [data, blockFilter],
  );

  const toggle = (key: string) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const go = (item: SetupChecklistItem, block: SetupChecklistBlock) => {
    const moduleId = item.goModuleId!;
    if (!canSeeModule(moduleId)) {
      notificationService.toast.warning('You cannot open that screen. Ask an administrator.');
      return;
    }
    // An outlet row opens the outlet window at the part that fixes it.
    if (item.goSection && block.scope === 'OUTLET' && block.id) requestOutletSection(block.id, item.goSection);
    onNavigateModule?.(moduleId);
  };

  return (
    <div className="module-master-container">
      <div className="setup-sheet">
        <div className="config-panel-header setup-checklist-header">
          <div>
            <h2>Setup Checklist</h2>
            <p>
              What still needs setting up before the hotel can operate. <strong>Missing</strong> stops an operation;
              a <strong>warning</strong> means the setup is probably incomplete. Nothing here blocks work.
            </p>
          </div>
          <div className="setup-checklist-tools">
            {data && (
              <span className={`badge ${data.missing > 0 ? 'badge-danger' : 'badge-success'}`}>
                {data.missing > 0 ? `${data.missing} missing` : 'Ready to operate'}
              </span>
            )}
            {data && data.warnings > 0 && <span className="badge badge-warning">{data.warnings} warnings</span>}
            <select className="setup-sheet-select" value={blockFilter} onChange={(e) => setBlockFilter(e.target.value)}>
              <option value="">Every property</option>
              {(data?.blocks ?? []).map((b) => <option key={blockKey(b)} value={blockKey(b)}>{b.name}</option>)}
            </select>
            {notNeededCount > 0 && (
              <label className="setup-checklist-toggle">
                <input type="checkbox" checked={showNotNeeded} onChange={(e) => setShowNotNeeded(e.target.checked)} />
                {notNeededCount} not needed
              </label>
            )}
            <button className="btn btn-secondary btn-sm" onClick={() => void load()} disabled={loading} title="Check again">
              <IconSystem.refresh size={16} />
            </button>
          </div>
        </div>

        {loading && !data && <div className="config-loading">Checking the setup...</div>}

        {blocks.map((b) => {
          const key = blockKey(b);
          const items = b.items.filter((i) => showNotNeeded || i.status !== 'NOT_NEEDED');
          const isCollapsed = collapsed.has(key);
          return (
            <section key={key} className="setup-sheet-section">
              <button type="button" className="setup-checklist-block" onClick={() => toggle(key)}>
                {isCollapsed ? <IconSystem.chevronRight size={16} /> : <IconSystem.chevronDown size={16} />}
                <span className="setup-checklist-block-name">{b.name}</span>
                {b.missing > 0 && <span className="badge badge-danger">{b.missing} missing</span>}
                {b.warnings > 0 && <span className="badge badge-warning">{b.warnings} warnings</span>}
                {b.missing + b.warnings === 0 && <span className="badge badge-success">Ready</span>}
              </button>
              {!isCollapsed && (
                <table className="setup-sheet-table">
                  <thead>
                    <tr><th style={{ width: 90 }}>Status</th><th>Check</th><th style={{ width: 110 }} /></tr>
                  </thead>
                  <tbody>
                    {items.map((i) => (
                      <tr key={i.code}>
                        <td><span className={`badge ${STATUS_CLASS[i.status]}`}>{STATUS_LABEL[i.status]}</span></td>
                        <td>
                          <span className="setup-sheet-note">{i.section} · {i.level === 'M' ? 'Mandatory' : i.level === 'R' ? 'Recommended' : 'Information'}</span>
                          <div>{i.title}</div>
                          {i.detail && <div className="setup-sheet-note">{i.detail}</div>}
                        </td>
                        <td>
                          {i.goModuleId && i.status !== 'DONE' && (
                            <button className="btn btn-link btn-sm" onClick={() => go(i, b)}>Go</button>
                          )}
                          {/* The help page of the screen that fixes it (design S6). */}
                          {i.goModuleId && (
                            <button className="btn btn-link btn-sm" onClick={() => openHelp(i.goModuleId!)} title="Help for this screen">Help</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

const blockKey = (b: SetupChecklistBlock) => `${b.scope}:${b.id ?? ''}`;

export default SetupChecklistScreen;
